<template>
  <div
    class="file_item_container flex items-center gap-2 px-3 py-3 rounded-lg group"
    :class="{ 'opacity-50': file.isDeleting }"
  >
    <!-- Drag Handle -->
    <div
      v-if="!disabled && !hideDragHandle"
      class="drag_handle cursor-grab active:cursor-grabbing flex-shrink-0"
    >
      <ThemeImage
        :icon="imageNames.DRAGGABLE_DOTS"
        alt="drag"
        class="size-4 opacity-50 group-hover:opacity-100"
      />
    </div>

    <!-- File Icon -->
    <div class="flex-shrink-0">
      <img
        :src="fileIcon"
        :alt="file.original_name"
        class="size-5"
      />
    </div>

    <!-- File Name -->
    <div class="flex-1 min-w-0">
      <span class="text-sm primary_color truncate block">
        {{ file.original_name || file.file_name }}
      </span>
    </div>

    <!-- Loading Spinner -->
    <CustomLoader
      v-if="file.isLoading || file.isDeleting"
      :size="16"
      :border-width="2"
      :show-label="false"
    />

    <!-- Actions Menu -->
    <CustomDropdown
      v-else-if="!disabled"
      ref="dropdownRef"
      position="end"
      dropdownCSS="p-1"
    >
      <template #trigger>
        <div
          class="flex items-center justify-center cursor-pointer p-1 rounded-md hover:bg-[var(--white-opacity-8)] opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ThemeImage
            :icon="imageNames.MORE_VERTICAL"
            alt="more"
            class="size-4"
          />
        </div>
      </template>

      <template #default>
        <div class="flex flex-col min-w-[120px]">
          <div
            class="dropdown_item flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[var(--white-opacity-4)] rounded-md"
            @click="handleDownload"
          >
            <ThemeImage
              :icon="imageNames.DOWNLOAD_GREY"
              alt="download"
              class="size-4"
            />
            <span class="text-sm primary_color">Download</span>
          </div>
          <div
            class="dropdown_item flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[var(--white-opacity-4)] rounded-md"
            @click="handleDeleteClick"
          >
            <ThemeImage
              :icon="imageNames.DELETE_GREY"
              alt="delete"
              class="size-4"
            />
            <span class="text-sm primary_color">Delete</span>
          </div>
        </div>
      </template>
    </CustomDropdown>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { getFileTypeIcon } from "@/src/constants/attachmentConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";

const props = defineProps({
  file: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hideDragHandle: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["download", "delete"]);

const dropdownRef = ref(null);

const fileIcon = computed(() => {
  const fileName = props.file.original_name || props.file.file_name || "";
  const extension = fileName.split(".").pop()?.toLowerCase();
  return getFileTypeIcon(extension);
});

const handleDownload = () => {
  dropdownRef.value?.close?.();
  emit("download", props.file);
};

const handleDeleteClick = () => {
  dropdownRef.value?.close?.();
  emit("delete", props.file);
};
</script>

<style scoped>
.file_item_container {
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

.file_item_container:hover {
  background: var(--white-opacity-4);
  border-color: var(--white-opacity-8);
}

.drag_handle:active {
  cursor: grabbing;
}
</style>

